import React, { useState, useEffect, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiLoader, FiUser, FiCheckCircle, FiXCircle, FiSearch, FiShield, FiEdit2 } = FiIcons;

const CUSTOM_ROLES = [
  { value: 'fan', label: 'Fan' },
  { value: 'artist', label: 'Artist' },
  { value: 'label_admin', label: 'Label Admin' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'support', label: 'Support' },
  { value: 'platform_admin', label: 'Platform Admin' }
];

export default function PlatformAdminUsersTable() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [processingId, setProcessingId] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // First fetch profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('d2f_profiles')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (profilesError) throw profilesError;
      
      const profiles = profilesData || [];
      const profileIds = profiles.map(p => p.id);
      
      let emailMap = {};
      
      // Then fetch emails securely via RPC if we have profiles
      if (profileIds.length > 0) {
        const { data: emailsData, error: emailsError } = await supabase
          .rpc('get_profile_emails_batch', { p_profile_ids: profileIds });
          
        if (emailsError) {
          console.error('[UsersTable] Error fetching emails:', emailsError);
        } else if (emailsData) {
          emailsData.forEach(e => {
            emailMap[e.profile_id] = e.email;
          });
        }
      }
      
      // Combine profile data with emails
      const usersWithEmail = profiles.map(p => ({
        ...p,
        email: emailMap[p.id] || 'N/A'
      }));
      
      setUsers(usersWithEmail);
    } catch (err) {
      console.error('[UsersTable] Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const toggleStatus = async (id, currentStatus) => {
    setProcessingId(id);
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      const { error } = await supabase
        .from('d2f_profiles')
        .update({ account_status: newStatus })
        .eq('id', id);
        
      if (!error) await fetchUsers();
    } catch (err) {
      console.error("Error toggling status", err);
    } finally {
      setProcessingId(null);
    }
  };

  const changeRole = async (id, newRole) => {
    if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) return;
    setProcessingId(id);
    try {
      const { error } = await supabase.rpc('update_user_role_admin', { p_user_id: id, p_new_role: newRole });
      if (error) throw error;
      await fetchUsers();
    } catch (err) {
      console.error("Error changing role", err);
      alert("Failed to change role. Ensure you have proper permissions.");
    } finally {
      setProcessingId(null);
    }
  };

  const filteredUsers = users.filter(user => 
    (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.display_name && user.display_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (user.role && user.role.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'platform_admin': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'moderator': return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      case 'support': return 'bg-teal-500/10 text-teal-400 border-teal-500/20';
      case 'label_admin': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'artist': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      default: return 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center bg-neutral-900 border border-neutral-800 p-4 rounded-xl">
        <div className="relative w-full max-w-md">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            placeholder="Search users by email, name, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-black border border-neutral-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
          />
        </div>
        <div className="text-sm text-neutral-400 ml-4">
          Showing {filteredUsers.length} users
        </div>
      </div>

      <div className="bg-neutral-900 border border-neutral-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12 text-neutral-400">
            <SafeIcon icon={FiLoader} className="animate-spin text-2xl mr-3" />
            Loading users...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-black/50 text-neutral-400">
                <tr>
                  <th className="px-6 py-4 font-medium">User</th>
                  <th className="px-6 py-4 font-medium">Role & Permissions</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium">Joined</th>
                  <th className="px-6 py-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-neutral-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt={user.display_name} className="w-full h-full object-cover" />
                          ) : (
                            <SafeIcon icon={FiUser} className="text-neutral-500" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-white">{user.display_name || 'Anonymous User'}</div>
                          <div className="text-xs text-neutral-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                          {user.role ? user.role.replace('_', ' ') : 'fan'}
                        </span>
                        
                        <div className="relative group">
                          <select 
                            className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
                            value={user.role || 'fan'}
                            onChange={(e) => changeRole(user.id, e.target.value)}
                            disabled={processingId === user.id}
                          >
                            {CUSTOM_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                          <button className="p-1.5 rounded-md bg-neutral-800 hover:bg-neutral-700 text-neutral-400 transition-colors pointer-events-none">
                            <SafeIcon icon={FiEdit2} className="text-xs" />
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${user.account_status === 'active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className={`text-xs ${user.account_status === 'active' ? 'text-green-400' : 'text-red-400'}`}>
                          {user.account_status || 'active'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-neutral-400">
                      <div className="text-xs">{new Date(user.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {processingId === user.id ? (
                          <SafeIcon icon={FiLoader} className="animate-spin text-neutral-400" />
                        ) : (
                          <>
                            {user.role !== 'platform_admin' ? (
                              <button 
                                onClick={() => toggleStatus(user.id, user.account_status || 'active')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border flex items-center gap-1.5 transition-colors ${
                                  user.account_status === 'active' 
                                    ? 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20' 
                                    : 'bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20'
                                }`}
                              >
                                {user.account_status === 'active' ? <><SafeIcon icon={FiXCircle} /> Suspend</> : <><SafeIcon icon={FiCheckCircle} /> Activate</>}
                              </button>
                            ) : (
                              <span className="text-xs text-neutral-500 flex items-center gap-1">
                                <SafeIcon icon={FiShield} /> Protected
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}